/**
 * Breadcrumbs — v2 (Spec §2.2).
 *
 * Klickbar, Truncate-Middle bei >3 Levels mit `…`.
 * Beispiel: Home > Rechnungen > … > Detail
 *
 * Tokens: text-text-secondary (Links), text-text-primary (Last/Active),
 * text-text-tertiary (Separator + Ellipsis).
 */
import { Link, useLocation } from "react-router-dom";
import { ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";

const LABELS: Record<string, string> = {
  dashboard: "Dashboard",
  inbox: "Inbox",
  invoices: "Rechnungen",
  workflows: "Workflows",
  suppliers: "Lieferanten",
  "payment-runs": "Payment-Runs",
  "bank-accounts": "Bankkonten",
  analytics: "Analytics",
  cashflow: "Cashflow",
  reports: "Reports",
  settings: "Einstellungen",
  users: "Nutzer",
  integrations: "Integrationen",
  audit: "Audit-Log",
  new: "Neu",
  edit: "Bearbeiten",
};

interface Crumb {
  label: string;
  href: string;
  isLast: boolean;
}

export function Breadcrumbs() {
  const { pathname } = useLocation();
  const parts = pathname.split("/").filter(Boolean);
  // Skip workspaces/:id (first 2 segments)
  const rest = parts.slice(2);
  if (rest.length === 0) return null;

  const homeBase = "/" + parts.slice(0, 2).join("/");

  const crumbs: Crumb[] = [
    { label: "Home", href: `${homeBase}/dashboard`, isLast: false },
    ...rest.map((seg, i) => ({
      label: LABELS[seg] || decodeURIComponent(seg),
      href: homeBase + "/" + rest.slice(0, i + 1).join("/"),
      isLast: i === rest.length - 1,
    })),
  ];

  // Truncate-Middle: bei >3 Levels (Home + 3+) zeigen wir Home > … > parent > current.
  // Spec §2.2: max 3 Levels + `…`.
  let displayCrumbs: (Crumb | "ellipsis")[] = crumbs;
  if (crumbs.length > 3) {
    displayCrumbs = [
      crumbs[0],
      "ellipsis",
      crumbs[crumbs.length - 2],
      crumbs[crumbs.length - 1],
    ];
  }

  return (
    <nav
      aria-label="Breadcrumb"
      className="flex items-center gap-1 text-small text-text-secondary"
    >
      {displayCrumbs.map((c, idx) => {
        const isFirst = idx === 0;
        if (c === "ellipsis") {
          return (
            <span key={`ellipsis-${idx}`} className="flex items-center gap-1">
              <ChevronRight
                className="h-3 w-3 text-text-tertiary"
                aria-hidden="true"
              />
              <span
                className="px-1 text-text-tertiary"
                aria-label="weitere Ebenen"
              >
                …
              </span>
            </span>
          );
        }
        return (
          <span key={c.href} className="flex items-center gap-1">
            {!isFirst && (
              <ChevronRight
                className="h-3 w-3 text-text-tertiary"
                aria-hidden="true"
              />
            )}
            {c.isLast ? (
              <span
                className={cn(
                  "max-w-[280px] truncate text-body-strong text-text-primary",
                )}
                aria-current="page"
              >
                {c.label}
              </span>
            ) : (
              <Link
                to={c.href}
                className="rounded-sm px-0.5 transition-colors duration-fast ease-out-quint hover:text-text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
              >
                {c.label}
              </Link>
            )}
          </span>
        );
      })}
    </nav>
  );
}
